import type { ReportRow } from '../../models/report.types'

import type {
  BuildCategoricalChartOptionsInput,
  CategoricalChartOptions,
} from './chartBuilder.types'
import { category, finiteNumber, groupRows } from './chartData'
import { chartFieldLabel } from './chartPresentation'
import { firstField, manyFields, type ResolvedChartFields } from './resolveChartFields'

function buildFunnel(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const dimension = firstField(fields, ['dimension'])
  const metric = firstField(fields, ['metric'])
  if (!dimension || !metric) return null
  return {
    chart: { type: 'funnel' },
    title: { text: '' },
    series: [
      {
        type: 'funnel',
        name: chartFieldLabel(input, metric),
        data: input.rows.map((row) => [category(row[dimension]), finiteNumber(row[metric])]),
      },
    ],
    credits: { enabled: false },
  } as CategoricalChartOptions
}

function buildWaterfall(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const dimension = firstField(fields, ['dimension'])
  const metric = firstField(fields, ['metric'])
  if (!dimension || !metric) return null
  return {
    chart: { type: 'waterfall' },
    title: { text: '' },
    xAxis: { type: 'category' },
    series: [
      {
        type: 'waterfall',
        name: chartFieldLabel(input, metric),
        data: input.rows.map((row) => ({
          name: category(row[dimension]),
          y: finiteNumber(row[metric]),
        })),
      },
    ],
    credits: { enabled: false },
  } as CategoricalChartOptions
}

function buildRadar(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const dimension = firstField(fields, ['dimension'])
  const metrics = manyFields(fields, ['metrics'])
  if (!dimension || metrics.length === 0) return null
  return {
    chart: { polar: true, type: 'line' },
    title: { text: '' },
    pane: { size: '80%' },
    xAxis: {
      categories: input.rows.map((row) => category(row[dimension])),
      tickmarkPlacement: 'on',
    },
    yAxis: { gridLineInterpolation: 'polygon', min: 0 },
    series: metrics.map((metric) => ({
      type: 'line',
      name: chartFieldLabel(input, metric),
      data: input.rows.map((row) => finiteNumber(row[metric])),
      pointPlacement: 'on',
    })),
    credits: { enabled: false },
  } as CategoricalChartOptions
}

function buildGauge(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const metric = firstField(fields, ['metric'])
  const row = input.rows[0]
  if (!metric || !row || input.rows.length !== 1) return null
  const value = finiteNumber(row[metric])
  return {
    chart: { type: 'gauge' },
    title: { text: '' },
    pane: { startAngle: -150, endAngle: 150 },
    yAxis: { min: 0, max: Math.max(1, value * 1.2) },
    series: [{ type: 'gauge', name: chartFieldLabel(input, metric), data: [value] }],
    credits: { enabled: false },
  } as CategoricalChartOptions
}

function sankeyData(
  rows: readonly ReportRow[],
  source: string,
  target: string,
  metric: string,
): Array<[string, string, number]> {
  const links = new Map<string, [string, string, number]>()
  for (const row of rows) {
    const from = category(row[source])
    const to = category(row[target])
    const key = JSON.stringify([from, to])
    const existing = links.get(key)
    links.set(key, [from, to, (existing?.[2] ?? 0) + finiteNumber(row[metric])])
  }
  return [...links.values()]
}

function buildSankey(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const source = firstField(fields, ['dimension_source'])
  const target = firstField(fields, ['dimension_target'])
  const metric = firstField(fields, ['metric'])
  if (!source || !target || !metric) return null
  return {
    chart: { type: 'sankey' },
    title: { text: '' },
    series: [
      {
        type: 'sankey',
        name: chartFieldLabel(input, metric),
        keys: ['from', 'to', 'weight'],
        data: sankeyData(input.rows, source, target, metric),
      },
    ],
    credits: { enabled: false },
  } as CategoricalChartOptions
}

function buildPackedBubble(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const dimensions = manyFields(fields, ['dimensions'])
  const metric = firstField(fields, ['metric'])
  const pointDimension = dimensions.at(-1)
  if (!pointDimension || !metric) return null
  const groupingDimension = dimensions.length > 1 ? dimensions[0] : undefined
  const groups = groupRows(input.rows, groupingDimension)
  return {
    chart: { type: 'packedbubble' },
    title: { text: '' },
    series: [...groups.entries()].map(([group, rows]) => ({
      type: 'packedbubble',
      name: group || chartFieldLabel(input, metric),
      data: rows.map((row) => ({
        name: category(row[pointDimension]),
        value: finiteNumber(row[metric]),
      })),
    })),
    credits: { enabled: false },
  } as CategoricalChartOptions
}

function buildLollipop(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const dimension = firstField(fields, ['dimension'])
  const metric = firstField(fields, ['metric'])
  if (!dimension || !metric) return null
  return {
    chart: { type: 'lollipop' },
    title: { text: '' },
    xAxis: { categories: input.rows.map((row) => category(row[dimension])) },
    series: [
      {
        type: 'lollipop',
        name: chartFieldLabel(input, metric),
        data: input.rows.map((row) => finiteNumber(row[metric])),
      },
    ],
    credits: { enabled: false },
  } as CategoricalChartOptions
}

export function buildAdvancedChartOptions(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  if (input.type === 'funnel') return buildFunnel(input, fields)
  if (input.type === 'waterfall') return buildWaterfall(input, fields)
  if (input.type === 'radar') return buildRadar(input, fields)
  if (input.type === 'gauge') return buildGauge(input, fields)
  if (input.type === 'sankey') return buildSankey(input, fields)
  if (input.type === 'packed_bubble') return buildPackedBubble(input, fields)
  if (input.type === 'lollipop') return buildLollipop(input, fields)
  return null
}
